import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Scissors, Zap, Shield, Star, Globe, Clock, Lock, Check, ArrowRight, TrendingUp, Users } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import { BASIC_PLAN, BASIC_ANNUAL_PLAN, MONTHLY_PLAN, ANNUAL_PLAN } from '../types/subscription';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import Auth from './Auth';
import { LegalModal } from './Legal';

export const PricingPage: React.FC = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user, profile } = useAuth();
  const [agreed, setAgreed] = React.useState(false);
  const [showAuth, setShowAuth] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(null);
  const [legalType, setLegalType] = React.useState<'terms' | 'privacy' | 'cookies' | null>(null);
  const [billingInterval, setBillingInterval] = React.useState<'month' | 'year'>('month');

  const handleSubscribe = async (planId: string) => {
    if (!agreed) {
      toast.error(t('booking.mustAgree') || 'You must agree to the Terms & Conditions and Privacy Policy.');
      return;
    }

    if (!user) {
      setSelectedPlanId(planId);
      setShowAuth(true);
      return;
    }

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          shopId: profile?.barber_shop_id,
          planId: planId,
          agreedToTerms: true
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Subscription Error:', error);
      toast.error(error.message || 'Something went wrong');
    }
  };

  const plans = [
    {
      ...(billingInterval === 'month' ? BASIC_PLAN : BASIC_ANNUAL_PLAN),
      icon: Users,
      color: 'zinc',
      popular: false,
      description: t('pricing.basicDesc')
    },
    {
      ...(billingInterval === 'month' ? MONTHLY_PLAN : ANNUAL_PLAN),
      icon: Zap,
      color: 'amber',
      popular: true,
      description: t('pricing.proDesc')
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-zinc-900'} py-24 px-6 overflow-hidden relative`}>
      <LegalModal 
        isOpen={!!legalType} 
        onClose={() => setLegalType(null)} 
        onAccept={() => {
          if (legalType === 'terms' || legalType === 'privacy') {
            setAgreed(true);
          }
          setLegalType(null);
        }}
        type={legalType || 'terms'} 
      />
      {/* Background Atmosphere */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-[-10%] left-[-10%] w-[40%] h-[40%] blur-[120px] rounded-full ${
          theme === 'dark' ? 'bg-amber-500/10' : 'bg-amber-500/5'
        }`} />
        <div className={`absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] blur-[120px] rounded-full ${
          theme === 'dark' ? 'bg-zinc-500/10' : 'bg-zinc-500/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 ${
              theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-zinc-50 border-zinc-200'
            }`}
          >
            <Star className="text-amber-500" size={14} />
            <span className={`text-[10px] font-black uppercase tracking-widest ${
              theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'
            }`}>Premium Partnership</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6 leading-[0.9]"
          >
            {t('pricing.title').split(' ').slice(0, -2).join(' ')} <br />
            <span className="text-amber-500">{t('pricing.title').split(' ').slice(-2).join(' ')}</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`max-w-2xl mx-auto text-lg font-medium ${
              theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'
            }`}
          >
            {t('pricing.subtitle')}
          </motion.p>
        </div>

        {/* Billing Toggle */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex items-center justify-center gap-4 mb-16"
        >
          <span className={`text-sm font-black uppercase tracking-widest transition-colors ${
            billingInterval === 'month' 
              ? (theme === 'dark' ? 'text-white' : 'text-zinc-900') 
              : 'text-zinc-500'
          }`}>
            {t('pricing.monthly')}
          </span>
          <button 
            onClick={() => setBillingInterval(prev => prev === 'month' ? 'year' : 'month')}
            aria-label={billingInterval === 'month' ? 'Switch to annual billing' : 'Switch to monthly billing'}
            className={`w-16 h-8 rounded-full relative p-1 transition-all ${
              theme === 'dark' ? 'bg-zinc-900 border border-zinc-800' : 'bg-zinc-100 border border-zinc-200'
            }`}
          >
            <motion.div 
              animate={{ x: billingInterval === 'month' ? 0 : 32 }}
              className="w-6 h-6 bg-amber-500 rounded-full shadow-lg"
            />
          </button>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-black uppercase tracking-widest transition-colors ${
              billingInterval === 'year' 
                ? (theme === 'dark' ? 'text-white' : 'text-zinc-900') 
                : 'text-zinc-500'
            }`}>
              {t('pricing.annually')}
            </span>
            <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-black uppercase tracking-widest rounded-full">
              {t('pricing.saveTwoMonths')}
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + idx * 0.1 }}
              className={`relative group ${plan.popular ? 'z-10' : 'z-0'}`}
            >
              {plan.popular && (
                <div className="absolute inset-0 bg-amber-500/20 blur-[60px] rounded-[4rem] group-hover:bg-amber-500/30 transition-all duration-500" />
              )}
              <div className={`relative border ${
                plan.popular 
                  ? 'border-amber-500/50' 
                  : (theme === 'dark' ? 'border-zinc-800' : 'border-zinc-200')
              } ${
                theme === 'dark' ? 'bg-zinc-900' : 'bg-white'
              } p-10 rounded-[3.5rem] shadow-2xl h-full flex flex-col`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-amber-500 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-xl shadow-amber-500/20 z-20">
                    {t('pricing.mostPopular')}
                  </div>
                )}

                <div className="mb-8">
                  <div className={`w-12 h-12 rounded-2xl ${
                    plan.color === 'amber' 
                      ? 'bg-amber-500/10 text-amber-500' 
                      : (theme === 'dark' ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-500')
                  } flex items-center justify-center mb-6`}>
                    <plan.icon size={24} aria-hidden="true" />
                  </div>
                  <h2 className={`text-2xl font-black uppercase tracking-tighter mb-2 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{plan.name}</h2>
                  <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-6">{plan.description}</p>
                  
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-black tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{plan.currency} {plan.price}</span>
                    <span className="text-zinc-500 text-xs font-black uppercase tracking-widest">
                      /{plan.interval === 'month' ? 'mo' : 'yr'}
                    </span>
                  </div>
                </div>

                <div className="space-y-4 mb-10 flex-grow">
                  {plan.features.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full ${
                        plan.color === 'amber' 
                          ? 'bg-amber-500/10 text-amber-500' 
                          : (theme === 'dark' ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-400')
                      } flex items-center justify-center shrink-0`}>
                        <Check size={12} />
                      </div>
                      <span className={`text-xs font-bold ${
                        theme === 'dark' ? 'text-zinc-400' : 'text-zinc-600'
                      }`}>
                        {feature === 'Priority Support' ? t('pricing.features.prioritySupport') : 
                         feature === 'Advanced Analytics' ? t('pricing.features.advancedAnalytics') :
                         feature === 'Unlimited Barbers' ? t('pricing.features.unlimitedBarbers') :
                         feature === 'Unlimited Bookings' ? t('pricing.features.unlimitedBookings') :
                         feature === 'Custom Services & Categories' ? t('pricing.features.customServices') :
                         feature === 'Real-time Dashboard' ? t('pricing.features.realtimeDash') :
                         feature === 'Email Notifications' ? t('pricing.features.emailNotifications') :
                         feature === 'Multi-language Support' ? t('pricing.features.multiLanguage') :
                         feature === 'Up to 3 Barbers' ? t('pricing.features.upTo3Barbers') :
                         feature === 'Standard Analytics' ? t('pricing.features.standardAnalytics') :
                         feature}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all duration-300 active:scale-95 ${
                    plan.popular 
                      ? 'bg-amber-500 text-white shadow-xl shadow-amber-500/20 hover:bg-amber-600' 
                      : 'bg-white text-black hover:bg-zinc-200'
                  }`}
                >
                  {t('pricing.startTrial')} <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto mt-16 px-4">
          <div className={`${
            theme === 'dark' ? 'bg-[#111111] border-[#222222]' : 'bg-zinc-50 border-zinc-200'
          } border p-4 sm:p-6 rounded-[2rem] sm:rounded-[4rem] flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden transition-colors`}>
            <div className="flex items-center gap-6 group flex-grow ml-2 w-full sm:w-auto relative z-10">
              <label className="relative flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <div className={`w-8 h-8 rounded-xl border-2 transition-all flex-shrink-0 flex items-center justify-center peer-focus:ring-2 ring-amber-500/50 ${
                  agreed 
                    ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/10' 
                    : (theme === 'dark' ? 'border-[#333] group-hover:border-[#555]' : 'border-zinc-300 group-hover:border-zinc-400')
                }`}>
                  {agreed && <Check className="text-amber-500" strokeWidth={3} size={20} />}
                </div>
              </label>
              
              <div className={`text-[10px] sm:text-xs font-black transition-colors uppercase tracking-[0.1em] flex flex-wrap items-center gap-x-1.5 gap-y-1 ${
                theme === 'dark' ? 'text-zinc-500' : 'text-zinc-400'
              }`}>
                <span 
                  className={`cursor-pointer transition-colors ${
                    theme === 'dark' ? 'hover:text-zinc-400' : 'hover:text-zinc-600'
                  }`}
                  onClick={() => setAgreed(!agreed)}
                >
                  {t('pricing.iAgreeTo')}
                </span>
                <button 
                  type="button"
                  onClick={() => setLegalType('terms')} 
                  className="text-[#f59e0b] hover:text-amber-400 transition-colors tracking-widest uppercase cursor-pointer"
                >
                  {t('common.terms')}
                </button>
                <span className="text-zinc-500">{t('pricing.and')}</span>
                <button 
                  type="button"
                  onClick={() => setLegalType('privacy')} 
                  className="text-[#f59e0b] hover:text-amber-400 transition-colors tracking-widest uppercase cursor-pointer"
                >
                  {t('common.privacy')}
                </button>
              </div>
            </div>
            <div className="flex items-center sm:pr-4 relative z-10 w-full sm:w-auto justify-end">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" 
                alt="Stripe" 
                className={`h-6 transition-all grayscale hover:grayscale-0 filter ${
                  theme === 'dark' ? 'opacity-40 hover:opacity-100' : 'opacity-60 hover:opacity-100'
                }`} 
                referrerPolicy="no-referrer" 
              />
            </div>
          </div>
          <p className="text-center mt-8 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600">
            {t('pricing.cancelAnytime')} • {t('pricing.noChargeToday')}
          </p>
        </div>
      </div>

      <AnimatePresence>
        {showAuth && !user && (
          <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 overflow-y-auto backdrop-blur-md ${
            theme === 'dark' ? 'bg-black/90' : 'bg-white/90'
          }`}>
            <div className="relative w-full max-w-xl my-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className={`border rounded-[3rem] p-8 sm:p-12 shadow-2xl relative ${
                  theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'
                }`}
              >
                <button 
                  onClick={() => setShowAuth(false)}
                  className={`absolute top-8 right-8 transition-colors ${
                    theme === 'dark' ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'
                  }`}
                >
                  <Lock size={20} />
                </button>
                
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Lock className="text-amber-500" size={32} aria-hidden="true" />
                  </div>
                  <h2 className={`text-3xl font-black uppercase tracking-tighter mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-zinc-900'
                  }`}>
                    {t('pricing.authRequired')}
                  </h2>
                  <p className={`font-medium ${
                    theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'
                  }`}>
                    {t('pricing.authDesc')}
                  </p>
                </div>

                <Auth 
                  onAuthSuccess={() => setShowAuth(false)} 
                  initialIsSignUp={true} 
                  role="barber_admin" 
                  planId={selectedPlanId || (billingInterval === 'year' ? 'annual' : 'monthly')}
                />
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
